/* NAV egyeztetés — a könyvelő és a NAV adófolyószámla összevetése (7. lépés).
 *
 * ⚠⚠ EZ NEM AZ ONLINE SZÁMLA. Az adófolyószámla-tükröt vetjük össze a könyvelőtől
 * kapott adó-bizonylatokkal (`EMAIL_TAX`).
 *
 * ⚠ **BIZONYLATOT NEM ÍR.** Ez a 3. fázis: megmutatja, hol tér el a két forrás.
 * A projekció (bizonylat-írás) a 8. lépés, `dry_run` + jóváhagyó UI-jal — a
 * sorrend szándékos: előbb lássuk, mennyire tér el a valóságban.
 *
 * A nézet SÚLYOSSÁG szerint rendez, nem adónem szerint: ez teendő-lista, nem
 * riport. A „mit tegyek" a `okok` mezőből derül ki, amit a motor emberi nyelven ad.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { RefreshCw, AlertTriangle, AlertCircle, Info, CheckCircle2,
         FilePlus2, Link2 } from 'lucide-react';
import { navUpoApi, documentsApi } from '../services/api';
import { Btn, Card, Alert, Amount, Input, Badge, Spinner, Checkbox,
         ConfirmDialog } from '../components/UI';
import { useToast } from '../components/Toast';
/* auto-PAID (9. lépés) — a MÁSIK írós művelet. Azért ide, a projekció mellé:
 * mindkettő „NAV-adatból bizonylat-állapot", és mindkettő két lépésben
 * (terv → jóváhagyás) megy. ⚠ A kár iránya viszont ELLENTÉTES: a projekció
 * hibája dupla utalás, ezé ELMARADT utalás. */
import NavSettle from '../components/NavSettle';

// A 6.3 tábla állapotai emberi névvel. A magyarázatot a motor `okok` mezője adja.
const STATUS_LABEL = {
  EGYEZIK:              'Egyezik',
  OSSZEG_ELTERES:       'Összeg eltérés',
  ESEDEKESSEG_ELTERES:  'Esedékesség eltérés',
  SZAMLASZAM_ELTERES:   'Számlaszám eltérés',
  CSAK_NAV:             'Csak a NAV szerint van',
  CSAK_KONYVELO:        'Csak a könyvelő szerint van',
  NAV_NAL_MEG_NEM:      'A NAV még nem könyvelte le',
  TULFIZETES:           'Túlfizetés',
};

const SEV = {
  kritikus:       { icon: AlertCircle,   color: 'var(--danger)',  label: 'kritikus' },
  figyelmeztetes: { icon: AlertTriangle, color: 'var(--warning)', label: 'figyelmeztetés' },
  informacio:     { icon: Info,          color: 'var(--primary)', label: 'információ' },
};

const fmtDate = (v) => v ? v.replace(/-/g, '.') + '.' : '—';
const todayYmd = () => new Date().toISOString().slice(0, 10);

function SevBadge({ sulyossag }) {
  const s = SEV[sulyossag] || SEV.informacio;
  const Icon = s.icon;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'0.7rem',
      fontWeight:600,color:s.color,whiteSpace:'nowrap'}}>
      <Icon size={12}/>{s.label}
    </span>
  );
}

export default function NavReconcile() {
  const toast = useToast();
  const [asOf, setAsOf] = useState(todayYmd());
  const [ablak, setAblak] = useState(12);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // ── Projekció (4. fázis) ────────────────────────────────────────────────
  // ⚠ Két lépés, SZÁNDÉKOSAN: előbb terv (`dry_run`), aztán jóváhagyás. Ez az
  // egyetlen NAV ÜPO művelet, ami bizonylatot ír — az pedig megjelenik az
  // Utalandó listán, tehát pénzmozgást eredményezhet.
  const [plan, setPlan] = useState(null);
  const [approved, setApproved] = useState({});     // item_key → bool
  const [applyResult, setApplyResult] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const runMut = useMutation({
    mutationFn: () => navUpoApi.reconcile({ as_of: asOf, ablak_honap: ablak }),
    onSuccess: (d) => { setResult(d); setError(null); setPlan(null);
                        setApplyResult(null); },
    onError: (e) => setError(e?.response?.data?.detail || e.message || 'Hiba'),
  });

  const planMut = useMutation({
    mutationFn: () => navUpoApi.projectPlan({ as_of: asOf }),
    onSuccess: (d) => {
      setPlan(d);
      // Alapból MINDEN jelölt be van pipálva — a felhasználó vesz ki, nem tesz
      // be. A kihagyás így tudatos döntés, nem feledékenység.
      const init = {};
      [...(d.new_documents || []), ...(d.links || [])]
        .forEach(r => { init[r.item_key] = true; });
      setApproved(init);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A terv nem készült el.'),
  });

  const applyMut = useMutation({
    mutationFn: (keys) => navUpoApi.projectApply(keys, { as_of: asOf }),
    onSuccess: (d) => {
      setApplyResult(d);
      setPlan(null);
      toast.success(`${d.created_count} új bizonylat, ${d.linked_count} kapcsolás.`);
      runMut.mutate();          // az összevetés újraszámol
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A projekció nem futott le.'),
  });

  const approvedKeys = Object.entries(approved)
    .filter(([, v]) => v).map(([k]) => k);

  const elteresek = result?.elteresek || [];
  const td = { padding: '6px 8px', verticalAlign: 'top' };

  return (
    <div>
      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a = confirm.action; setConfirm(null); a(); }}/>

      <div style={{marginBottom:'1.1rem'}}>
        <h1 style={{fontSize:'1.2rem',fontWeight:700}}>NAV egyeztetés</h1>
        <p style={{fontSize:'0.78rem',color:'var(--text-muted)',marginTop:2}}>
          A könyvelőtől kapott adó-bizonylatok összevetése a NAV adófolyószámlájával.
          Ez a nézet <b>nem ír bizonylatot</b> — csak megmutatja, hol tér el a két forrás.
        </p>
      </div>

      <Card title="Összevetés indítása"
        subtitle="Az „állapot napja” az, amelyik napra vonatkozóan a NAV-adatot értelmezzük; az időablak a könyvelő bizonylatainak visszamenőleges köre.">
        <div style={{display:'flex',gap:10,alignItems:'end',flexWrap:'wrap'}}>
          <Input label="Állapot napja" type="date" lang="hu" value={asOf}
            onChange={e => setAsOf(e.target.value)} style={{maxWidth:190}}/>
          <Input label="Időablak (hónap)" type="number" min={1} max={60} value={ablak}
            onChange={e => setAblak(Number(e.target.value) || 12)} style={{maxWidth:150}}/>
          <Btn icon={RefreshCw} loading={runMut.isPending} disabled={!asOf}
            onClick={() => { setResult(null); setError(null); runMut.mutate(); }}>
            Összevetés
          </Btn>
        </div>
        {error && <Alert type="danger" style={{marginTop:12}}>{error}</Alert>}
      </Card>

      {runMut.isPending && (
        <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}>
          <Spinner size={24}/>
        </div>
      )}

      {result && (
        <>
          <Card title="Összegzés">
            <div style={{display:'grid',
              gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:14}}>
              <div>
                <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>Kritikus</div>
                <div style={{fontSize:'1.4rem',fontWeight:700,fontFamily:'var(--mono)',
                  color: result.kritikus_db ? 'var(--danger)' : 'var(--success)'}}>
                  {result.kritikus_db}
                </div>
              </div>
              <div>
                <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>Eltérés összesen</div>
                <div style={{fontSize:'1.4rem',fontWeight:700,fontFamily:'var(--mono)'}}>
                  {elteresek.length}
                </div>
              </div>
              <div>
                <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>Könyvelő tételei</div>
                <div style={{fontSize:'1.4rem',fontWeight:700,fontFamily:'var(--mono)'}}>
                  {result.konyvelo_db}
                </div>
              </div>
              <div>
                <div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>NAV tételei</div>
                <div style={{fontSize:'1.4rem',fontWeight:700,fontFamily:'var(--mono)'}}>
                  {result.nav_tetel_db}
                </div>
              </div>
            </div>

            {/* ⚠ A settling-ablak default FELTEVÉS, nem mért érték — ezt ki KELL írni,
                különben a „NAV még nem könyvelte le” állapot ténynek tűnik. */}
            <p style={{fontSize:'0.72rem',color:'var(--text-dim)',marginTop:14,marginBottom:0}}>
              Settling-ablak: <b>{result.settling_napok} nap</b>
              {result.settling_is_default && ' (feltevés — a NAV-oldali könyvelési '
                + 'késleltetést élesben kell megmérni, nem kérdezni)'}.
              {result.wrote_flags && ' A nyitott/rendezett jelölés frissült: '
                + `${result.flags?.nyitott ?? 0} nyitott, ${result.flags?.rendezett ?? 0} rendezett.`}
            </p>

            {(result.figyelmeztetesek || []).length > 0 && (
              <Alert type="warning" style={{marginTop:12}}>
                <b>Adatminőség:</b>
                <ul style={{marginTop:6,paddingLeft:18,marginBottom:0,fontSize:'0.78rem'}}>
                  {result.figyelmeztetesek.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </Alert>
            )}
          </Card>

          <Card title="Eltérések" subtitle="Súlyosság szerint rendezve. A „Miért” oszlop mondja meg, mi a teendő."
            noPad>
            {elteresek.length === 0 ? (
              <div style={{padding:'1.6rem',textAlign:'center',color:'var(--text-muted)'}}>
                <CheckCircle2 size={22} style={{color:'var(--success)',marginBottom:8}}/>
                <p style={{fontSize:'0.85rem',margin:0}}>
                  Nincs eltérés a könyvelő és a NAV adatai között ebben az időablakban.
                </p>
              </div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',fontSize:'0.78rem',borderCollapse:'collapse'}}>
                  <thead><tr style={{textAlign:'left',color:'var(--text-muted)',
                    borderBottom:'1px solid var(--border)'}}>
                    <th style={td}>Súlyosság</th>
                    <th style={td}>Állapot</th>
                    <th style={td}>Adónem</th>
                    <th style={{...td,textAlign:'right'}}>NAV szerint</th>
                    <th style={{...td,textAlign:'right'}}>Könyvelő szerint</th>
                    <th style={td}>Esedékesség</th>
                    <th style={td}>Miért</th>
                  </tr></thead>
                  <tbody>
                    {elteresek.map((e, i) => (
                      <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={td}><SevBadge sulyossag={e.sulyossag}/></td>
                        <td style={td}>
                          <Badge variant={
                            e.status === 'EGYEZIK' ? 'success'
                            : e.sulyossag === 'kritikus' ? 'danger'
                            : e.sulyossag === 'figyelmeztetes' ? 'warning' : 'default'}>
                            {STATUS_LABEL[e.status] || e.status}
                          </Badge>
                        </td>
                        <td style={td}>
                          {e.adonem_megnevezes || e.adonem_kod || '—'}
                          {e.adonem_kod && e.adonem_megnevezes && (
                            <span style={{color:'var(--text-dim)',fontFamily:'var(--mono)',
                              fontSize:'0.72rem'}}> · {e.adonem_kod}</span>)}
                        </td>
                        <td style={{...td,textAlign:'right'}}>
                          <Amount value={e.nav_osszeg} currency="HUF"/></td>
                        <td style={{...td,textAlign:'right'}}>
                          <Amount value={e.konyvelo_osszeg} currency="HUF"/></td>
                        <td style={td}>
                          {e.nav_esedekesseg || e.konyvelo_esedekesseg ? (
                            <span style={{whiteSpace:'nowrap'}}>
                              {fmtDate(e.nav_esedekesseg)}
                              {e.konyvelo_esedekesseg &&
                               e.konyvelo_esedekesseg !== e.nav_esedekesseg && (
                                <span style={{color:'var(--warning)'}}>
                                  {' ↔ '}{fmtDate(e.konyvelo_esedekesseg)}
                                </span>)}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{...td,color:'var(--text-muted)',maxWidth:340}}>
                          {(e.okok || []).join(' · ')}
                          {/* ⚠ A számlaszám-eltérés a legdrágább hiba: rossz számlára
                              utalást okozna, ezért kiírjuk mindkét értéket. */}
                          {e.status === 'SZAMLASZAM_ELTERES' && (
                            <div style={{marginTop:4,fontFamily:'var(--mono)',
                              fontSize:'0.72rem'}}>
                              NAV: {e.nav_szamlaszam || '—'}<br/>
                              könyvelő: {e.konyvelo_szamlaszam || '—'}
                            </div>
                          )}
                          {e.document_id && (
                            <div style={{marginTop:4}}>
                              <a href={`/documents/${e.document_id}`}
                                 style={{fontSize:'0.74rem'}}>bizonylat megnyitása →</a>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* ── Projekció (4. fázis) ── */}
          <Card title="Bizonylatok készítése a NAV-adatból"
            subtitle="A NAV szerinti, de a könyvelőnél nem szereplő kötelezettségekből bizonylat készül; ahol van könyvelői rekord, ott a NAV-adat CSAK hozzákapcsolódik — új bizonylat nem keletkezik."
            actions={
              <Btn size="sm" variant="ghost" icon={FilePlus2}
                loading={planMut.isPending}
                onClick={() => { setApplyResult(null); planMut.mutate(); }}>
                Terv megtekintése
              </Btn>}>
            {applyResult ? (
              <Alert type="success">
                Elkészült: <b>{applyResult.created_count}</b> új bizonylat,{' '}
                <b>{applyResult.linked_count}</b> kapcsolás a meglévő rekordokhoz.
                {applyResult.created_count > 0 &&
                  <> Az új bizonylatok a <a href="/documents">Bizonylatok</a> listán
                     jelennek meg.</>}
              </Alert>
            ) : !plan ? (
              <p style={{fontSize:'0.82rem',color:'var(--text-muted)',marginBottom:0}}>
                A „Terv megtekintése" megmutatja, mi keletkezne — <b>írás nélkül</b>.
                Bizonylat csak a jóváhagyás után készül.
              </p>
            ) : (
              <>
                {plan.new_count === 0 && plan.link_count === 0 ? (
                  <Alert type="info">
                    Nincs mit kivetíteni: minden NAV-tétel vagy már ki van vetítve,
                    vagy nem igényel bizonylatot.
                    {plan.skipped_count > 0 && ` (${plan.skipped_count} kihagyva.)`}
                  </Alert>
                ) : (
                  <>
                    {plan.new_count > 0 && (
                      <>
                        <p style={{fontSize:'0.76rem',fontWeight:600,
                          color:'var(--text-muted)',marginBottom:8,marginTop:4}}>
                          <FilePlus2 size={12}/> ÚJ BIZONYLAT ({plan.new_count}) — a NAV
                          tud a kötelezettségről, a könyvelő nem
                        </p>
                        {plan.new_documents.map(r => (
                          <div key={r.item_key} style={{display:'flex',gap:10,
                            alignItems:'flex-start',padding:'8px 0',
                            borderTop:'1px solid var(--border)'}}>
                            <Checkbox checked={!!approved[r.item_key]}
                              onChange={e => setApproved(a => ({ ...a,
                                [r.item_key]: e.target.checked }))}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:'0.82rem',fontWeight:500}}>
                                {r.supplier_name}
                                <span style={{color:'var(--text-dim)',
                                  fontFamily:'var(--mono)',fontSize:'0.74rem'}}>
                                  {' · '}{r.adonem_kod}</span>
                              </div>
                              <div style={{fontSize:'0.74rem',color:'var(--text-muted)'}}>
                                esedékesség: {fmtDate(r.esedekesseg)} · számlaszám:{' '}
                                <span style={{fontFamily:'var(--mono)'}}>
                                  {r.szamlaszam || '—'}</span>
                                {r.item_kind === 'KONYVELESRE_VARO' &&
                                  <> · <Badge>könyvelésre váró</Badge></>}
                              </div>
                              {/* ⚠ Célszámla nélkül nem lehet utalni — és NEM
                                  tippelünk helyette. */}
                              {r.warning && (
                                <div style={{fontSize:'0.74rem',color:'var(--warning)',
                                  marginTop:2}}>⚠ {r.warning}</div>
                              )}
                            </div>
                            <div style={{textAlign:'right',whiteSpace:'nowrap'}}>
                              <Amount value={r.osszeg} currency="HUF"/>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {plan.link_count > 0 && (
                      <>
                        <p style={{fontSize:'0.76rem',fontWeight:600,
                          color:'var(--text-muted)',marginBottom:8,marginTop:16}}>
                          <Link2 size={12}/> KAPCSOLÁS ({plan.link_count}) — a meglévő
                          bizonylat kapja meg a NAV-adatot, új nem keletkezik
                        </p>
                        {plan.links.map(r => (
                          <div key={r.item_key} style={{display:'flex',gap:10,
                            alignItems:'flex-start',padding:'8px 0',
                            borderTop:'1px solid var(--border)'}}>
                            <Checkbox checked={!!approved[r.item_key]}
                              onChange={e => setApproved(a => ({ ...a,
                                [r.item_key]: e.target.checked }))}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:'0.82rem',fontWeight:500}}>
                                {r.adonem_megnevezes || r.adonem_kod}
                                {' → '}
                                <a href={`/documents/${r.document_id}`}>
                                  #{r.document_id}</a>
                              </div>
                              <div style={{fontSize:'0.74rem',color:'var(--text-muted)'}}>
                                {STATUS_LABEL[r.status] || r.status}
                                {(r.okok || []).length > 0 &&
                                  <> · {r.okok.join(' · ')}</>}
                              </div>
                            </div>
                            <div style={{textAlign:'right',whiteSpace:'nowrap',
                              fontSize:'0.76rem'}}>
                              NAV: <Amount value={r.nav_osszeg} currency="HUF"/>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    <div style={{display:'flex',justifyContent:'space-between',
                      alignItems:'center',gap:10,marginTop:16,flexWrap:'wrap'}}>
                      <span style={{fontSize:'0.74rem',color:'var(--text-muted)'}}>
                        {approvedKeys.length} tétel jóváhagyva
                        {plan.skipped_count > 0 &&
                          ` · ${plan.skipped_count} kihagyva (már ki van vetítve)`}
                      </span>
                      <div style={{display:'flex',gap:8}}>
                        <Btn variant="ghost" onClick={()=>setPlan(null)}>Mégse</Btn>
                        <Btn disabled={!approvedKeys.length} loading={applyMut.isPending}
                          onClick={()=>setConfirm({
                            title:'Bizonylatok készítése',
                            confirmLabel:'Létrehozás',
                            message:`${approvedKeys.length} tétel kivetítése. Az új `
                                  + 'bizonylatok megjelennek az Utalandó listán, tehát '
                                  + 'utalás alapjául szolgálhatnak. Folytatod?',
                            action:()=>applyMut.mutate(approvedKeys),
                          })}>
                          Jóváhagyás és létrehozás
                        </Btn>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </Card>
        </>
      )}

      {/* ── Befizetések visszacsatolása (9. lépés) ──
          ⚠ SZÁNDÉKOSAN a `{result && …}` blokkon KÍVÜL: az auto-PAID nem az
          összevetés eredményére épül, hanem a MÁR bizonylathoz párosított
          NAV-tételekre. Ha a feltételes blokkban lenne, csak összevetés
          futtatása után jelenne meg — pedig attól teljesen független. */}
      <NavSettle/>

      {/* ── B11 + A13 (2026-09-06): NAV Online Számla teljesség — bejövő + kimenő ── */}
      <NavCompleteness/>
    </div>
  );
}

function NavCompleteness() {
  const toast = useToast();
  const prev = new Date(); prev.setDate(1); prev.setMonth(prev.getMonth() - 1);
  const [month, setMonth] = useState(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
  const [res, setRes] = useState(null);
  const mut = useMutation({
    mutationFn: () => documentsApi.navCompleteness(month),
    onSuccess: (d) => setRes(d),
    onError: (e) => toast.error(e?.response?.data?.detail || 'A teljesség-egyeztetés nem futott le.'),
  });
  const td = { padding: '5px 8px', verticalAlign: 'top', fontSize: '0.8rem', borderBottom: '1px solid var(--border-dim)' };
  const list = (rows, kind) => rows?.length ? (
    <table style={{width:'100%', borderCollapse:'collapse'}}>
      <thead><tr>
        {['Számlaszám', 'Partner', 'Kelt', 'Bruttó', kind === 'nav' ? 'NAV-művelet' : 'Forrás / státusz'].map(h => (
          <th key={h} style={{...td, textAlign:'left', color:'var(--text-muted)', fontSize:'0.66rem', textTransform:'uppercase'}}>{h}</th>))}
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={{...td, fontFamily:'var(--mono)'}}>{r.invoice_number}</td>
            <td style={td}>{r.partner || r.supplier_name || r.customer_name || '–'}</td>
            <td style={{...td, fontFamily:'var(--mono)'}}>{r.issue_date || '–'}</td>
            <td style={{...td, fontFamily:'var(--mono)', textAlign:'right'}}>{r.gross_amount != null ? Number(r.gross_amount).toLocaleString('hu-HU') : '–'} {r.currency || ''}</td>
            <td style={td}>{kind === 'nav' ? (r.nav_operation || '–') : `${r.source || ''} · ${r.status || ''}${r.billingo_online_szamla_status ? ' · NAV: ' + r.billingo_online_szamla_status : ''}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  ) : <div style={{fontSize:'0.8rem', color:'var(--success)'}}>Nincs eltérés.</div>;
  const block = (title, d, navOnlyLabel, oursOnlyLabel) => d?.error ? (
    <Alert type="warning">{title}: {d.error}</Alert>
  ) : d ? (
    <Card title={title} subtitle={`NAV: ${d.nav_count} · nálunk: ${d.ours_count} · egyezik: ${d.matched}`} style={{marginBottom:'0.75rem'}}>
      <div style={{fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:6}}>
        <b>{navOnlyLabel}</b> ({d.nav_only.length} · {Number(d.nav_only_total).toLocaleString('hu-HU')}) — {d.meaning?.nav_only}
      </div>
      {list(d.nav_only, 'nav')}
      <div style={{fontSize:'0.72rem', color:'var(--text-muted)', margin:'12px 0 6px'}}>
        <b>{oursOnlyLabel}</b> ({d.ours_only.length} · {Number(d.ours_only_total).toLocaleString('hu-HU')}) — {d.meaning?.ours_only}
      </div>
      {list(d.ours_only, 'ours')}
    </Card>
  ) : null;
  return (
    <Card title="NAV Online Számla — teljesség-egyeztetés (bejövő + kimenő)"
      subtitle="Megvan-e minden bizonylat a hónapra? A NAV digest-listája a mi bizonylataink ellen, mindkét irányban. Kimenő oldalon a NAV a mi nevünkben ismert számlákat adja: ami nálunk van és ott nincs, arról nincs adatszolgáltatás; ami ott van és nálunk nincs, azt nem mi állítottuk ki, vagy hiányzik a szinkron. Csak összevetés, nem ír bizonylatot; 2 NAV-hívás (+ lapok)."
      style={{marginTop:'0.75rem'}}>
      <div style={{display:'flex', gap:8, alignItems:'end', marginBottom:10}}>
        <Input label="Hónap" type="month" value={month} onChange={e => setMonth(e.target.value)}/>
        <Btn icon={RefreshCw} loading={mut.isPending} onClick={() => mut.mutate()}>Egyeztetés</Btn>
      </div>
      {res && (<>
        {block('Bejövő számlák', res.inbound, 'A NAV tud róla, mi nem', 'Nálunk van, a NAV-nál nincs')}
        {block('Kimenő számlák (A13)', res.outbound, 'A NAV a nevünkben ismeri, nálunk NINCS', 'Kimenő számlánk adatszolgáltatás NÉLKÜL')}
      </>)}
    </Card>
  );
}
